
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { graphqlSchema } from '../../services/graphql.schema';
import { ToBillArguments, ToBillEvent } from '../../models/Factura';
import Loading from '../Animations/loadScreen';
import Pagination from '../Forms/Pagination';
import DashboardBillEvent from './Dashboard.billEvent';
import CloseBillEventConfirm from './Dashboard.billEventList.closeConfirm';

import './Dashboard.billEventList.scss';
import sharedToasterSubject from '../../services/shared.toasterSubject';

const ITEMS_PER_PAGE = 15;

interface DashboardBillEventListProps {
    onEmitBillEvent: (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => void;
    onEmitPago: (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => void;
}

function DashboardBillEventList({ onEmitBillEvent, onEmitPago }: DashboardBillEventListProps) {
    const [openBillArgs, setOpenBillArgs] = useState<ToBillArguments<ToBillEvent>[]>([]);
    const [openBillEvents, setOpenBillEvents] = useState<ToBillEvent[]>([]);
    const [openBillsLoaded, setOpenBillsLoaded] = useState(false);
    const [page, setPage] = useState(1);
    const [pendingCloseEvent, setPendingCloseEvent] = useState<ToBillEvent | null>(null);

    useQuery(graphqlSchema.FISCALPOP.CFDI.getOpenBillArguments, {
        onCompleted: ({ getOpenBillArguments }: { getOpenBillArguments: ToBillArguments<ToBillEvent>[] }) => {
            console.log('getOpenBillArguments: ', getOpenBillArguments);
            setOpenBillArgs(getOpenBillArguments);
            const openBillEvents = getOpenBillArguments
                .reduce((p, c) => {
                    p = p.concat(c.billEvents, c.paymentEvents);
                    return p;
                }, [] as ToBillEvent[])
                .filter(pE => (!!pE.CFDI || !!pE.paymentCFDI) && !(pE.isClosed || pE.wasMuted))
                .sort((a, b) => (a.when || 0) - (b.when || 0))
            setOpenBillEvents(openBillEvents);
            setOpenBillsLoaded(true);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [setEventToClosed] = useMutation(graphqlSchema.FISCALPOP.CFDI.setBillEventToClosed, {
        onCompleted: ({ setBillEventToClosed }: { setBillEventToClosed: { _id: string, isClosed: boolean } }) => {
            if (setBillEventToClosed) {
                setOpenBillEvents(prev => prev.filter(pE => pE._id !== setBillEventToClosed._id));
                sharedToasterSubject.next({ type: 'confirm', message: `Evento marcado como cerrado. No se mostrarán más notificaciones de este evento.` })
            }
        },
        onError: (err) => {
            console.error('Error setting bill event to closed: ', err);
        }
    });

    const [setEventToMuted] = useMutation(graphqlSchema.FISCALPOP.CFDI.setBillEventToMute, {
        onCompleted: ({ setBillEventToMute }: { setBillEventToMute: { _id: string, isMuted: boolean } }) => {
            if (setBillEventToMute) {
                setOpenBillEvents(prev => prev.filter(pE => pE._id !== setBillEventToMute._id));
                sharedToasterSubject.next({ type: 'confirm', message: `Evento silenciado. No se mostrarán más notificaciones de este evento.` })
            }
        },
        onError: (err) => {
            console.error('Error setting bill event to muted: ', err);
        }
    });

    const handleCloseBillEvent = (billEvent: ToBillEvent, _parentArgument: ToBillArguments<ToBillEvent>) => {
        setPendingCloseEvent(billEvent);
    }

    const handleConfirmClose = () => {
        if (pendingCloseEvent) {
            setEventToClosed({ variables: { billEventId: pendingCloseEvent._id } });
        }
        setPendingCloseEvent(null);
    }

    const handleCancelClose = () => {
        setPendingCloseEvent(null);
    }

    const handleMuteBillEvent = (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => {
        setEventToMuted({ variables: { billEventId: billEvent._id } });
    }

    console.log('rendering DashboardBillEventList: ', { openBillArgs, openBillEvents, openBillsLoaded });
    console.log('Filter Results: ', openBillEvents
        .filter((_, i) => (i >= (ITEMS_PER_PAGE * (page - 1)) && (i < (ITEMS_PER_PAGE * page)))))

    return (
        <>
            <CloseBillEventConfirm
                isOpen={!!pendingCloseEvent}
                onCancel={handleCancelClose}
                onConfirm={handleConfirmClose}
            />
            <div className="card table">
                <div className="cardTitle">
                    <div className="header billPreview">
                        <p className="cliente">Cliente</p>
                        <p className="tipo">Tipo de factura</p>
                        <p className="monto">Monto</p>
                    </div>
                </div>
                <div className="cardBody">
                    {
                        !openBillsLoaded &&
                        <Loading svgHeight={'40px'} display={true} relativePos={true} />
                    }
                    {
                        (openBillsLoaded && !openBillEvents.length) &&
                        <div className="noPreviews">
                            <p>
                                No hay facturas pendientes de revisar
                            </p>
                        </div>
                    }
                    {
                        openBillEvents
                            .filter((_, i) => (i >= (ITEMS_PER_PAGE * (page - 1)) && (i < (ITEMS_PER_PAGE * page))))
                            .map((billEv, i) => {
                                const parentArgument = openBillArgs.find(bA => bA._id === billEv.argumentId);
                                return (
                                    <DashboardBillEvent
                                        key={i}
                                        billEvent={billEv}
                                        parentArgument={parentArgument}
                                        onEmitBillEvent={onEmitBillEvent}
                                        onEmitPago={onEmitPago}
                                        onCloseBillEvent={handleCloseBillEvent}
                                        onMuteBillEvent={handleMuteBillEvent}
                                    />
                                );
                            })
                    }
                    <Pagination page={page} pages={Math.ceil(openBillEvents.length / ITEMS_PER_PAGE)} onPage={setPage} />
                </div>
            </div>
        </>
    );
}

export default DashboardBillEventList;
